/**
 * @file BinScanField.tsx
 * @description Поле ячейки: скан или адрес ячейки, полки, стеллажа
 *   (phase19.22, W14-9; ui-design §8) — вместо `BinSelectField` в приёмке,
 *   перемещении и размещении.
 *
 * Одно поле на скан и ручной ввод (правило warehouse-UI): USB-сканер печатает
 * `LOC:A-02-03`, кладовщик набирает `a-2-3` или `A-02` руками — поле само
 * допишет префикс и спросит сервер, что это за уровень (`useBinScan`).
 * Выбранная ячейка — карточка «✓ A-02-03 (2 партии) [×]», уровень — плитки
 * `BinLevelPicker`, ошибка — янтарной строкой, фокус остаётся в поле.
 *
 * Пустое значение — законный вариант («по умолчанию у фасовки», «Без места»):
 * его подпись задаёт вызывающий, и он всегда виден под полем.
 *
 * ```tsx
 * <BinScanField warehouseId={wh} value={binId} onChange={setBinId}
 *   bins={bins} emptyOptionLabel="— по умолчанию у фасовки —" />
 * ```
 */

import { Check, X } from "lucide-react";
import { useState } from "react";

import { BinLevelPicker } from "./BinLevelPicker";
import { useBinScan, type PickedBin } from "./useBinScan";
import { Button } from "../../ui/button";
import { ScanField } from "../../ui/ScanField";
import type { StorageBinDto } from "../../../lib/api-bins";
import { pluralRu } from "../../../lib/plural";

interface Props {
  warehouseId: string;
  /** id ячейки; пустая строка — адрес не задан. */
  value: string;
  onChange: (binId: string) => void;
  /** Справочник склада — подпись выбранной ячейки по id. */
  bins?: StorageBinDto[];
  /** Код выбранной ячейки, если справочника нет (строка документа). */
  valueCode?: string | null;
  /** Подпись пустого значения: «Без места», «— по умолчанию у фасовки —». */
  emptyOptionLabel?: string;
  disabled?: boolean;
  /** В строке таблицы: без подсказки под полем. */
  compact?: boolean;
  /** Взять фокус при открытии — окно «Указать ячейку» (phase19.31, Б16). */
  autoFocus?: boolean;
}

export function BinScanField({
  warehouseId,
  value,
  onChange,
  bins,
  valueCode,
  emptyOptionLabel,
  disabled = false,
  compact = false,
  autoFocus = false,
}: Props) {
  const [picked, setPicked] = useState<PickedBin | null>(null);
  const scan = useBinScan(warehouseId, (bin) => {
    setPicked(bin);
    onChange(bin.id);
  });

  const known = picked?.id === value ? picked : null;
  const code = known?.code ?? valueCode ?? bins?.find((b) => b.id === value)?.code ?? null;

  if (value) {
    return (
      <div className="flex min-h-[var(--control-h,3rem)] items-center gap-2 rounded-lg bg-primary/8 px-3">
        <Check className="size-5 shrink-0 text-primary" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="font-mono text-base">
            {code ?? "ячейка выбрана"}
            {known && known.lotsCount > 0
              ? ` (${known.lotsCount} ${pluralRu(known.lotsCount, ["партия", "партии", "партий"])})`
              : ""}
          </span>
          {known?.note ? <span className="text-xs text-on-surface-variant">{known.note}</span> : null}
        </span>
        <Button
          type="button"
          variant="ghost"
          className="size-12 shrink-0 p-0"
          onClick={() => onChange("")}
          disabled={disabled}
          aria-label={emptyOptionLabel ? `Сбросить: ${emptyOptionLabel}` : "Сбросить ячейку"}
        >
          <X className="size-5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ScanField
        mode="scanAndSearch"
        autoFocus={autoFocus}
        busy={scan.busy}
        disabled={disabled}
        placeholder="Скан или адрес: A-02-03"
        showFeedbackIndicator={false}
        helperText={compact ? undefined : "Ячейка, полка (A-02) или стеллаж (A) — Enter"}
        onQueryChange={() => scan.clearError()}
        onSubmit={(raw) => void scan.resolve(raw)}
      />
      {scan.error ? <p className="text-xs text-warning">{scan.error}</p> : null}
      {scan.level ? (
        <BinLevelPicker
          level={scan.level}
          onPick={(bin) => scan.pick(bin)}
          onOpenShelf={scan.openShelf}
          onClose={scan.closeLevel}
        />
      ) : null}
      {emptyOptionLabel && !compact ? (
        <span className="text-xs text-on-surface-variant">Сейчас: {emptyOptionLabel}</span>
      ) : null}
    </div>
  );
}
